import React, { FC } from 'react';
import { BasketRentPaymentSelectionProps } from '@components/v2/BasketRentPaymentSelection';
import useTranslations from '@hooks/useTranslations';
import { BUDGET_TYPE_RENT } from '../../../services';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import { useRouter } from 'next/router';
import { Link as LinkV2 } from '@components/v2/link';
import Link from '@components/Link';
import { isBrandAC, LANGUAGE, LEASYS_PROVIDER_TYPE, MARKET } from '../../../constants/main';
import { useDispatch, useSelector } from 'react-redux';
import FinanceWidgetDuck from '../../../redux/financeWidget/financeWidget.duck';
import PriceSuffix from '@components/PriceSuffix';
import FinanceCalculatorService from '../../../services/financeQuote/financePrices.service';
import { localeToCurrency } from '@utils/localeToCurrency';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { ICalculateSummaryRequestData } from '../../../services/calculateSummary/calculateSummary.types';
import { useSummaryDataPromise } from '@components/OfferListItem/hooks/useSummaryDataPromise';
import { useCarStockJourneyCheck } from '@hooks/useCarStockJourneyCheck';
import { usePrices } from '@hooks/usePriceCalculator';
import { useFinanceWidgetLogic } from '@hooks/useFinanceWidgetLogic';
import { getMonthlyPriceObjByType } from '@utils/Price.utils';
import { StockFormatedPriceType } from '../../../services/stock/models/stockItem.price.model';

const BasketRentPaymentSelectionTemplate: FC<BasketRentPaymentSelectionProps> = ({
    isSelected,
    onClick,
    className,
}) => {
    const { t } = useTranslations();

    const router = useRouter();

    const dispatch = useDispatch();

    const { isStockJourney } = useCarStockJourneyCheck();

    const { getClientType } = useFinanceWidgetLogic(0);

    const { formatMoney } = useMoneyFormatter();

    const { currentDeal, carDetails } = useCarDetailsDuck();

    const { catalog } = usePrices(carDetails, true, null, true);

    const currentFinanceQuote = useSelector(FinanceWidgetDuck.getLeasysQuote);

    const carConfigId = isStockJourney ? carDetails?.externalId : carDetails?._id;
    const params: ICalculateSummaryRequestData = isStockJourney
        ? { carConfigId, token: currentFinanceQuote?.token, carConfiguration: carDetails }
        : { carConfigId, token: currentFinanceQuote?.token };

    const amountRounding = getMonthlyPriceObjByType(carDetails.prices, StockFormatedPriceType.EMPLOYEE)?.amountRounding;

    const { processCalculateSummaryDataPromise } = useSummaryDataPromise(params, LEASYS_PROVIDER_TYPE);

    return (
        <div className={className}>
            <button
                className={`section ${isSelected ? 'selected' : ''}`}
                onClick={() => {
                    onClick(BUDGET_TYPE_RENT);
                }}
                data-testid="TESTING_SELECTION_RENT"
            >
                <div className="inner">
                    <span className="title">
                        <span className={`icon ${isSelected ? 'selected' : ''}`}>
                            <span className={isSelected ? 'selected' : ''} />
                        </span>{' '}
                        {t('basket.payment.rent.title')}
                    </span>
                    <span className="description">{t('basket.payment.rent.description')}</span>
                    <div className="summary">
                        <span className="totalPrice">
                            {formatMoney(currentFinanceQuote?.monthlyPayment, false, '', amountRounding)}{' '}
                            <PriceSuffix checkPrioritizedTranslation isMonthlyPrice />
                        </span>
                        {isBrandAC ? (
                            <LinkV2
                                txt={t('summary.financeDetail')}
                                onClick={(event: Event) => {
                                    event.stopPropagation();
                                    processCalculateSummaryDataPromise();
                                }}
                                isUnderline
                                style={{ marginTop: '-10px' }}
                            />
                        ) : (
                            <Link
                                label={t('summary.financeDetail')}
                                onClick={(event: Event) => {
                                    event.stopPropagation();
                                    processCalculateSummaryDataPromise();
                                }}
                                marginBottom={20}
                                secondary
                            />
                        )}
                        {isSelected && (
                            <>
                                <div className="otherPrices">
                                    <div className="otherPrice">
                                        <span>{t('finance.duration')}</span>
                                        <span>
                                            <strong>
                                                {currentFinanceQuote?.duration} {t('finance.duration.unit')}
                                            </strong>
                                        </span>
                                    </div>
                                    <div className="otherPrice">
                                        <span>{t('finance.annualMileage')}</span>
                                        <span>
                                            <strong>
                                                {currentFinanceQuote?.annualMileage} {t('finance.annualMileage.units')}
                                            </strong>
                                        </span>
                                    </div>
                                </div>
                                <p
                                    className="description"
                                    dangerouslySetInnerHTML={{ __html: t('rent.legal.text.long') }}
                                />
                                <Link
                                    label={t('basket.payment.rent.personalize')}
                                    onClick={() =>
                                        // @ts-ignore
                                        showModalWidget({
                                            context: {
                                                quotationID: '',
                                                countryCode: MARKET.toUpperCase(),
                                                languageCode: LANGUAGE,
                                                siteCode: 'SOL',
                                                brand: 'Peugeot',
                                                componentCode: 'Widget',
                                                customer: {
                                                    clientType: getClientType(currentDeal),
                                                },
                                            },
                                            vehicle: {
                                                manufacturerCode: carDetails?.lcdv16,
                                                pricing: {
                                                    basicPriceExclTax: catalog?.totalPrice,
                                                },
                                                features: {
                                                    kind: 'VP',
                                                },
                                            },
                                            outputFn: async (output: any) => {
                                                console.log('OUTPUT', output);

                                                const { data } = await FinanceCalculatorService.updateFinanceQuote(
                                                    {
                                                        carConfigId: carDetails?.externalId,
                                                        financeProductKey: output?.result?.teaser?.data?.productKey,
                                                        annualMileage: output?.result?.teaser?.data?.annualMileage,
                                                        duration: output?.result?.teaser?.data?.duration,
                                                        currency: localeToCurrency(LANGUAGE),
                                                        deposit: output?.result?.teaser?.data?.depositWithVat,
                                                        isPersonalized: true,
                                                        providerType: LEASYS_PROVIDER_TYPE,
                                                        quotationID: output?.result?.quotationID,
                                                    },
                                                    currentDeal.financeQuoteId
                                                );
                                            },
                                            parameters: {
                                                cashDeposit: currentFinanceQuote?.deposit,
                                                duration: currentFinanceQuote?.duration,
                                                globalMileage: currentFinanceQuote?.annualMileage,
                                                productKey: currentFinanceQuote?.financeProductKey,
                                            },
                                        })
                                    }
                                    withArrowIcon
                                    primary={!isBrandAC}
                                    secondary={isBrandAC}
                                    marginBottom={20}
                                />
                            </>
                        )}
                    </div>
                </div>
            </button>
        </div>
    );
};

export default BasketRentPaymentSelectionTemplate;
